/**
 * Mode hors ligne côté API.
 *
 * Lectures : certaines réponses GET (liste des formations de l'apprenant, contenu des formations à jouer)
 * sont gardées dans IndexedDB et servies quand le réseau manque.
 *
 * Écritures : la progression (démarrage, avancement, fin de formation) part dans une file d'attente
 * (« outbox ») si le réseau manque, puis est rejouée dans l'ordre, en arrière-plan, au retour de la connexion.
 * Tant que la file n'est pas vide, les nouvelles écritures passent derrière pour garder l'ordre des événements.
 */
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { idbCount, idbDelete, idbGet, idbGetAll, idbGetAllKeys, idbPut, isIndexedDbAvailable } from './idb';

/** GET mis en cache pour l'usage hors ligne (chemins relatifs à `/api`). */
const READ_RULES: RegExp[] = [
  /^\/eduflows\/my$/,
  /^\/eduflows\/my\/institution-public$/,
  /^\/eduflows\/me\/evaluations$/,
  /^\/learning-paths\/my$/,
  /^\/podcasts\/my\/list$/,
  /^\/auth\/me\/learner-gamification$/,
  /^\/eduflows\/[0-9a-f-]{36}\/player-kind$/,
  /^\/eduflows\/[0-9a-f-]{36}\/play$/,
  /^\/eduflows\/[0-9a-f-]{36}\/play\/locale-meta$/,
  /^\/eduflows\/[0-9a-f-]{36}\/simple\/play$/,
];

/** Écritures mises en file d'attente si le réseau manque. */
const WRITE_RULES: RegExp[] = [/^\/eduflows\/[0-9a-f-]{36}\/play\/(start|progress|complete)$/];

const START_RULE = /^\/eduflows\/[0-9a-f-]{36}\/play\/start$/;

type CachedResponse = { data: unknown; savedAt: string; complete: boolean };

export type OutboxItem = {
  id?: number;
  userId: string;
  method: 'post';
  url: string;
  data: unknown;
  queuedAt: string;
  /** Échecs serveur (5xx, 401, 408, 429) ; les coupures réseau ne comptent pas. */
  attempts: number;
  /** Prochaine tentative (délai croissant après un échec serveur). */
  nextAttemptAt?: number;
  /** Abandonné après trop d'échecs ou refus définitif : n'empêche plus la synchronisation du reste. */
  failed?: boolean;
  failedReason?: string;
};

export type OfflineSyncStatus = {
  online: boolean;
  pending: number;
  /** Enregistrements de progression qui n'ont pas pu être synchronisés (refus du serveur). */
  failed: number;
  syncing: boolean;
  lastSyncAt: string | null;
  lastSyncedCount: number;
};

/** Au-delà, un élément en échec serveur est mis de côté pour ne pas bloquer la file. */
const MAX_SERVER_ATTEMPTS = 6;

let status: OfflineSyncStatus = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  pending: 0,
  failed: 0,
  syncing: false,
  lastSyncAt: null,
  lastSyncedCount: 0,
};
const listeners = new Set<(s: OfflineSyncStatus) => void>();

function setStatus(patch: Partial<OfflineSyncStatus>): void {
  status = { ...status, ...patch };
  listeners.forEach((fn) => {
    try {
      fn(status);
    } catch {
      /* listener isolé */
    }
  });
}

export function getOfflineSyncStatus(): OfflineSyncStatus {
  return status;
}

export function subscribeOfflineSyncStatus(fn: (s: OfflineSyncStatus) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Identifiant de l'utilisateur connecté, lu dans le jeton (sans dépendre du store, pour éviter les cycles). */
export function currentOfflineUserId(): string | null {
  try {
    const token = localStorage.getItem('auth_token');
    const part = token?.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { userId?: unknown; sub?: unknown };
    if (typeof payload.userId === 'string') return payload.userId;
    if (typeof payload.sub === 'string') return payload.sub;
  } catch {
    /* jeton absent ou illisible */
  }
  return null;
}

/** Rôles « apprenant » : seuls leurs contenus de lecteur sont mémorisés automatiquement (pas les formateurs, admins…). */
const LEARNER_ROLES = new Set(['student', 'learner', 'participant', 'collaborateur', 'collaborator']);

function currentOfflineRole(): string | null {
  try {
    const part = localStorage.getItem('auth_token')?.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { role?: unknown };
    return typeof payload.role === 'string' ? payload.role.toLowerCase() : null;
  } catch {
    return null;
  }
}

function pathOf(url: string): string {
  const noQuery = url.split('?')[0] ?? url;
  // Tolère une URL absolue ou préfixée par /api.
  const withoutOrigin = noQuery.replace(/^https?:\/\/[^/]+/, '');
  return withoutOrigin.replace(/^\/api(?=\/)/, '');
}

export function isOfflineReadable(url: string): boolean {
  const path = pathOf(url);
  return READ_RULES.some((re) => re.test(path));
}

export function isOfflineQueueable(url: string): boolean {
  const path = pathOf(url);
  return WRITE_RULES.some((re) => re.test(path));
}

export function isOfflineStartUrl(url: string): boolean {
  return START_RULE.test(pathOf(url));
}

function langOf(url: string, params?: Record<string, unknown>): string {
  const fromParams = params && typeof params.lang === 'string' ? params.lang : '';
  if (fromParams) return fromParams;
  const m = /[?&]lang=([^&]+)/.exec(url);
  return m ? decodeURIComponent(m[1]) : '';
}

function chapterOf(url: string, params?: Record<string, unknown>): string {
  const fromParams = params && typeof params.chapterId === 'string' ? params.chapterId : '';
  if (fromParams) return fromParams;
  const m = /[?&]chapterId=([^&]+)/.exec(url);
  return m ? decodeURIComponent(m[1]) : '';
}

/** Clé : utilisateur | chemin | chapitre (formation simple, chargée chapitre par chapitre) | langue. */
function responseKey(userId: string, url: string, params: Record<string, unknown> | undefined): string {
  return `${userId}|${pathOf(url)}|${chapterOf(url, params)}|${langOf(url, params)}`;
}

/** Erreur réseau (aucune réponse du serveur), au format normalisé par l'intercepteur axios. */
export function isNetworkFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { statusCode?: unknown; response?: unknown; request?: unknown; code?: unknown };
  if (e.statusCode === 0) return true;
  if (e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') return true;
  return Boolean(e.request) && !e.response && e.statusCode === undefined;
}

export function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Mémorise une réponse GET. `complete` = contenu intégral (téléchargement explicite) : une réponse partielle
 * (traductions différées via `prioritizeBlock`) ne remplace jamais une réponse complète.
 */
export async function rememberResponse(
  url: string,
  params: Record<string, unknown> | undefined,
  data: unknown,
  options?: { complete?: boolean },
): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  const userId = currentOfflineUserId();
  if (!userId) return;
  // Contenu de lecteur : seulement pour les apprenants ou un téléchargement explicite (évite d'empiler en local
  // chaque formation ouverte par un formateur ou un administrateur).
  if (!options?.complete && /^\/eduflows\/[0-9a-f-]{36}\//.test(pathOf(url))) {
    const role = currentOfflineRole();
    if (!role || !LEARNER_ROLES.has(role)) return;
  }
  const key = responseKey(userId, url, params);
  const partial = Boolean(params && params.prioritizeBlock);
  try {
    if (partial) {
      const existing = await idbGet<CachedResponse>('responses', key);
      if (existing) return;
    }
    const value: CachedResponse = { data, savedAt: new Date().toISOString(), complete: Boolean(options?.complete) || !partial };
    await idbPut('responses', value, key);
  } catch {
    /* quota dépassé, navigation privée : on continue sans cache */
  }
}

/** Réponse mémorisée pour l'utilisateur courant : même langue d'abord, sinon n'importe quelle langue. */
export async function cachedResponse(url: string, params?: Record<string, unknown>): Promise<unknown | undefined> {
  if (!isIndexedDbAvailable()) return undefined;
  const userId = currentOfflineUserId();
  if (!userId) return undefined;
  try {
    const exact = await idbGet<CachedResponse>('responses', responseKey(userId, url, params));
    if (exact?.complete) return exact.data;
    // Même chapitre dans une autre langue (contenu téléchargé dans la langue de l'élève au moment du téléchargement) :
    // une réponse complète est préférée à une réponse partielle dans la langue demandée.
    const prefix = `${userId}|${pathOf(url)}|${chapterOf(url, params)}|`;
    const keys = await idbGetAllKeys('responses');
    let partialOther: CachedResponse | undefined;
    for (const k of keys) {
      if (typeof k !== 'string' || !k.startsWith(prefix)) continue;
      const row = await idbGet<CachedResponse>('responses', k);
      if (row?.complete) return row.data;
      if (row && !partialOther) partialOther = row;
    }
    if (exact) return exact.data;
    if (partialOther) return partialOther.data;
  } catch {
    /* IndexedDB indisponible */
  }
  return undefined;
}

/** Supprime les réponses mémorisées dont le chemin commence par `pathPrefix` (ex. suppression d'un téléchargement). */
export async function forgetResponses(pathPrefix: string): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  const userId = currentOfflineUserId();
  if (!userId) return;
  const prefix = `${userId}|${pathPrefix}`;
  const keys = await idbGetAllKeys('responses');
  await Promise.all(keys.filter((k) => typeof k === 'string' && k.startsWith(prefix)).map((k) => idbDelete('responses', k)));
}

/** Réponse synthétique renvoyée au code appelant quand une écriture part dans la file. */
function syntheticResponse(url: string, data: unknown): unknown {
  if (isOfflineStartUrl(url)) {
    const sessionId = data && typeof data === 'object' ? (data as { clientSessionId?: unknown }).clientSessionId : undefined;
    return { success: true, offline: true, data: { sessionId: typeof sessionId === 'string' ? sessionId : null } };
  }
  return { success: true, offline: true, data: { queued: true } };
}

export async function refreshPendingCount(): Promise<number> {
  if (!isIndexedDbAvailable()) return 0;
  try {
    const userId = currentOfflineUserId();
    const all = await idbGetAll<OutboxItem>('outbox');
    const mine = userId ? all.filter((item) => item.userId === userId) : [];
    const pending = mine.filter((item) => !item.failed).length;
    setStatus({ pending, failed: mine.length - pending });
    return pending;
  } catch {
    return status.pending;
  }
}

/** Faut-il mettre cette écriture en file (réseau absent, ou file non vide pour garder l'ordre) ? */
export function shouldQueueBeforeSending(url: string): boolean {
  if (!isOfflineQueueable(url) || !isIndexedDbAvailable()) return false;
  return isBrowserOffline() || status.pending > 0;
}

function eventTypeOf(data: unknown): string | null {
  return data && typeof data === 'object' && typeof (data as { eventType?: unknown }).eventType === 'string'
    ? ((data as { eventType: string }).eventType)
    : null;
}

function sessionOf(data: unknown): unknown {
  return data && typeof data === 'object' ? (data as { sessionId?: unknown }).sessionId ?? null : null;
}

/** Identifiant unique d'écriture : le serveur ignore un rejeu déjà traité (idempotence). */
function newClientEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/** Ajoute un identifiant d'événement client aux métadonnées (progression) ou au corps (start / complete). */
export function withClientEventId(url: string, data: unknown): unknown {
  const body = data && typeof data === 'object' ? { ...(data as Record<string, unknown>) } : {};
  if (/\/play\/progress$/.test(pathOf(url))) {
    const metadata = body.metadata && typeof body.metadata === 'object' ? { ...(body.metadata as Record<string, unknown>) } : {};
    if (typeof metadata.clientEventId !== 'string') metadata.clientEventId = newClientEventId();
    return { ...body, metadata };
  }
  if (typeof body.clientEventId !== 'string') body.clientEventId = newClientEventId();
  return body;
}

export async function enqueueWrite(url: string, data: unknown): Promise<unknown> {
  const userId = currentOfflineUserId();
  if (!userId || !isIndexedDbAvailable()) throw { message: 'offline_queue_unavailable', statusCode: 0 };
  const path = pathOf(url);
  const body = withClientEventId(url, data ?? {});
  // Instantané périodique (toutes les 15 s dans le lecteur) : remplacer le précédent s'il est le dernier de la file
  // pour la même formation et la même session. Évite des centaines d'envois au retour du réseau.
  if (eventTypeOf(body) === 'progress_sync') {
    try {
      const mine = (await idbGetAll<OutboxItem>('outbox'))
        .filter((item) => item.userId === userId && !item.failed)
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      const last = mine[mine.length - 1];
      if (last && last.url === path && eventTypeOf(last.data) === 'progress_sync' && sessionOf(last.data) === sessionOf(body)) {
        await idbPut('outbox', { ...last, data: body, queuedAt: new Date().toISOString() });
        return syntheticResponse(url, body);
      }
    } catch {
      /* on ajoute simplement l'élément */
    }
  }
  const item: OutboxItem = {
    userId,
    method: 'post',
    url: path,
    data: body,
    queuedAt: new Date().toISOString(),
    attempts: 0,
  };
  await idbPut('outbox', item);
  setStatus({ pending: status.pending + 1 });
  return syntheticResponse(url, body);
}

function withReplayMetadata(item: OutboxItem): unknown {
  const body = item.data && typeof item.data === 'object' ? { ...(item.data as Record<string, unknown>) } : {};
  if (isOfflineStartUrl(item.url)) {
    // `start` : le corps est lui-même la métadonnée de session.
    return { ...body, offlineReplay: true, offlineQueuedAt: item.queuedAt };
  }
  const metadata =
    body.metadata && typeof body.metadata === 'object' ? { ...(body.metadata as Record<string, unknown>) } : {};
  return { ...body, metadata: { ...metadata, offlineReplay: true, offlineQueuedAt: item.queuedAt } };
}

let flushing: Promise<number> | null = null;

/**
 * Rejoue la file de l'utilisateur courant, dans l'ordre. S'arrête au premier échec réseau ou serveur
 * (nouvelle tentative plus tard). Une erreur 4xx définitive (hors 401 / 408 / 429) retire l'élément.
 */
export function flushOutbox(axiosInstance: AxiosInstance): Promise<number> {
  if (flushing) return flushing;
  // Un seul onglet rejoue la file à la fois (sinon doublons quand l'espace apprenant et le lecteur sont ouverts).
  const locks = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { locks?: { request: <T>(name: string, cb: () => Promise<T>) => Promise<T> } }).locks
    : undefined;
  const run = () => flushOutboxUnlocked(axiosInstance);
  flushing = (locks ? locks.request('lyrra-offline-outbox', run) : run()).finally(() => {
    flushing = null;
  });
  return flushing;
}

function flushOutboxUnlocked(axiosInstance: AxiosInstance): Promise<number> {
  return (async () => {
    if (!isIndexedDbAvailable() || isBrowserOffline()) return 0;
    const userId = currentOfflineUserId();
    if (!userId) return 0;
    let sent = 0;
    try {
      const all = (await idbGetAll<OutboxItem>('outbox'))
        .filter((item) => item.userId === userId && !item.failed)
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      if (all.length === 0) {
        setStatus({ pending: 0 });
        return 0;
      }
      setStatus({ syncing: true });
      for (const item of all) {
        if (item.nextAttemptAt && item.nextAttemptAt > Date.now()) break; // délai d'attente en cours : garder l'ordre
        try {
          const config: AxiosRequestConfig & { __lyrraOfflineReplay?: boolean } = {
            method: item.method,
            url: item.url,
            data: withReplayMetadata(item),
            timeout: 60_000,
          };
          config.__lyrraOfflineReplay = true;
          await axiosInstance.request(config);
          await idbDelete('outbox', item.id as number);
          sent += 1;
          setStatus({ pending: Math.max(0, status.pending - 1) });
        } catch (error) {
          const code = (error as { statusCode?: number })?.statusCode ?? 0;
          if (code === 0) break; // réseau : nouvelle tentative au retour de la connexion
          const retryable = code === 401 || code === 408 || code === 429 || code >= 500;
          const attempts = item.attempts + 1;
          if (retryable && attempts < MAX_SERVER_ATTEMPTS) {
            // Délai croissant : 30 s, 1 min, 2 min, 4 min, 8 min.
            await idbPut('outbox', { ...item, attempts, nextAttemptAt: Date.now() + 30_000 * 2 ** (attempts - 1) });
            break;
          }
          // Refus définitif (formation supprimée, accès retiré…) ou trop d'échecs : mis de côté, signalé à l'élève,
          // et la suite de la file continue.
          console.warn('[offline] écriture mise de côté', item.url, code);
          await idbPut('outbox', { ...item, attempts, failed: true, failedReason: String(code) });
        }
      }
    } finally {
      await refreshPendingCount();
      setStatus({
        syncing: false,
        ...(sent > 0 ? { lastSyncAt: new Date().toISOString(), lastSyncedCount: sent } : {}),
      });
    }
    return sent;
  })();
}

let started = false;

/** Démarre la synchronisation en arrière-plan (retour du réseau, retour sur l'onglet, minuteur). */
export function startOfflineSync(axiosInstance: AxiosInstance): void {
  if (started || typeof window === 'undefined' || !isIndexedDbAvailable()) return;
  started = true;
  const tryFlush = () => {
    if (status.pending > 0 && !isBrowserOffline()) void flushOutbox(axiosInstance);
  };
  window.addEventListener('online', () => {
    setStatus({ online: true });
    void refreshPendingCount().then(tryFlush);
  });
  window.addEventListener('offline', () => setStatus({ online: false }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tryFlush();
  });
  window.setInterval(() => {
    void refreshPendingCount().then(tryFlush);
  }, 60_000);
  // Message du service worker (Background Sync) : rejouer la file.
  navigator.serviceWorker?.addEventListener?.('message', (event: MessageEvent) => {
    if (event.data && event.data.type === 'LYRRA_SYNC_OUTBOX') tryFlush();
  });
  void refreshPendingCount().then(() => window.setTimeout(tryFlush, 3000));
}

/** Nombre total d'éléments en file (tous comptes), pour le diagnostic. */
export function totalOutboxCount(): Promise<number> {
  return isIndexedDbAvailable() ? idbCount('outbox') : Promise.resolve(0);
}
